import React, { useEffect, useRef, useState } from "react";
import KC_Text from "../../designsystem/typography/KC_Text/KC_Text";

import { faTimes } from "@fortawesome/free-solid-svg-icons";
import KC_Icon_Button from "../../designsystem/KC_Button/KC_Icon_Button";
import KC_Separator from "../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../designsystem/ds_commons";
import KC_Heading3 from "../../designsystem/typography/KC-Heading/KC_Heading3";

const DIM = 450;

const ImageGrid: React.FC<ImageGridProps> = ({
  imageUrl,
  gridSize = 10,
  onColorPick,
  colorKeyName,
  onClose
}) => {
  const [selectedCell, setSelectedCell] = useState<GridPosition | null>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [canvasError, setCanvasError] = useState<boolean>(false);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [surroundingPixels, setSurroundingPixels] = useState<any[][]>([]);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0
  });

  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const magnifierSize = 11;

  useEffect(() => {
    const img = imgRef.current;
    if (img) {
      const handleLoad = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
        setupCanvas();
        calculateImageTransform();
      };

      if (img.complete) {
        handleLoad();
      } else {
        img.addEventListener("load", handleLoad);
        return () => img.removeEventListener("load", handleLoad);
      }
    }
  }, [imageUrl]);

  const calculateImageTransform = () => {
    if (!imgRef.current) return;

    const naturalWidth = imgRef.current.naturalWidth;
    const naturalHeight = imgRef.current.naturalHeight;

    const scale = Math.min(DIM / naturalWidth, DIM / naturalHeight);
    setImageScale(scale);

    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;

    // Center image in container
    const offsetX = (DIM - scaledWidth) / 2;
    const offsetY = (DIM - scaledHeight) / 2;

    setImageOffset({ x: offsetX, y: offsetY });
  };

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;

    if (!canvas || !img) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    try {
      ctx.drawImage(img, 0, 0);
      ctx.getImageData(0, 0, 1, 1);
      setCanvasError(false);
    } catch (error) {
      console.warn(
        "Canvas color picking not available due to CORS restrictions"
      );
      setCanvasError(true);
    }
  };

  const getActualPixelCoordinates = (clientX: number, clientY: number) => {
    if (!containerRef.current || !imgRef.current) return { x: 0, y: 0 };

    const rect = containerRef.current.getBoundingClientRect();

    // Calculate position relative to container
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    // Convert to image coordinates accounting for offset and scale
    const imageX = Math.floor((relativeX - imageOffset.x) / imageScale);
    const imageY = Math.floor((relativeY - imageOffset.y) / imageScale);

    // Clamp coordinates to image boundaries
    return {
      x: Math.max(0, Math.min(imageX, imageDimensions.width - 1)),
      y: Math.max(0, Math.min(imageY, imageDimensions.height - 1))
    };
  };

  const getPixelColor = (x: number, y: number): string => {
    const canvas = canvasRef.current;
    if (!canvas) return "#fff";

    const ctx = canvas.getContext("2d");
    if (!ctx) return "#fff";

    try {
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      if (pixel[3] === 0) {
        return "#fff";
      }
      return (
        "#" +
        ("0" + pixel[0].toString(16)).slice(-2) +
        ("0" + pixel[1].toString(16)).slice(-2) +
        ("0" + pixel[2].toString(16)).slice(-2)
      );
    } catch {
      return "#fff";
    }
  };

  const handleGridClick = (
    event: React.MouseEvent<HTMLDivElement>,
    row: number,
    col: number
  ) => {
    const { x, y } = getActualPixelCoordinates(event.clientX, event.clientY);
    setSelectedCell({ row, col });

    if (onColorPick && !canvasError) {
      const color = getPixelColor(x, y);
      onColorPick(color, x, y);
    }
  };

  const handleGridHover = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const { x, y } = getActualPixelCoordinates(event.clientX, event.clientY);

      if (x < 0 || y < 0) return;

      setMousePosition({ x: event.clientX, y: event.clientY });

      if (!canvasError) {
        const color = getPixelColor(x, y);
        setHoveredColor(color);

        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          const pixels = getSurroundingPixels(ctx, x, y, magnifierSize);
          setSurroundingPixels(pixels);
        }
      }
    },
    [canvasError, getActualPixelCoordinates, getPixelColor]
  );

  const renderMagnifier = () => {
    if (!mousePosition || !surroundingPixels.length) return null;

    const pixelSize = 20;
    const magnifierWidth = magnifierSize * pixelSize;
    const centerIdx = Math.floor(magnifierSize / 2);

    return (
      <div
        style={{
          position: "fixed",
          left: mousePosition.x + 20,
          top: mousePosition.y + 20,
          width: magnifierWidth,
          height: magnifierWidth,
          backgroundColor: "white",
          border: "2px solid #ddd",
          borderRadius: "50%",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          overflow: "hidden",
          zIndex: 1001
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "grid",
            gridTemplateColumns: `repeat(${magnifierSize}, ${pixelSize}px)`,
            transform: "scale(1.2)"
          }}
        >
          {surroundingPixels.map((row, y) =>
            row.map((pixel, x) => (
              <div
                key={`${y}-${x}`}
                className={y === centerIdx && x === centerIdx ? "x-shape" : ""}
                style={{
                  width: pixelSize,
                  height: pixelSize,
                  backgroundColor: `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3] / 255})`,
                  outline:
                    y === centerIdx && x === centerIdx
                      ? "2px solid #EA6A9E"
                      : "none",
                  outlineOffset: "-2px"
                }}
              />
            ))
          )}
        </div>
      </div>
    );
  };

  const renderGrid = () => {
    const rows = Math.ceil(DIM / gridSize);
    const cols = Math.ceil(DIM / gridSize);

    return (
      <div
        className="position-absolute"
        style={{
          width: DIM,
          height: DIM,
          top: 0,
          left: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${gridSize}px)`,
          gridTemplateRows: `repeat(${rows}, ${gridSize}px)`
        }}
        onMouseMove={handleGridHover}
        onMouseLeave={() => {
          setHoveredColor(null);
          setMousePosition(null);
          setSurroundingPixels([]);
        }}
      >
        {Array.from({ length: rows }).map((_, rowIndex) =>
          Array.from({ length: cols }).map((_, colIndex) => (
            <div
              key={`${rowIndex}-${colIndex}`}
              onClick={(e) => handleGridClick(e, rowIndex, colIndex)}
              style={{
                border: "1px solid rgba(108, 117, 125, 0.5)",
                backgroundColor:
                  selectedCell?.row === rowIndex &&
                  selectedCell?.col === colIndex
                    ? "rgba(0, 123, 255, 0.1)"
                    : "transparent",
                cursor: "crosshair"
              }}
            />
          ))
        )}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          zIndex: 999
        }}
      />
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 6,
          zIndex: 1000,
          backgroundColor: "white",
          border: "6px solid rgba(0,0,0,0.5)"
        }}
      >
        <div className="w-100 text-center d-flex justify-content-between align-items-center p-2">
          <div className="flex-grow-1 d-flex justify-content-center">
            <KC_Heading3
              text={`Cliquer sur un carré pour sélectionner la ${colorKeyName?.toLowerCase()} `}
            />
          </div>

          <KC_Icon_Button isInverted={true} icon={faTimes} onClick={onClose} />
        </div>
        <KC_Separator hasLine={true} size={KC_Sizes.sm} />
        <div
          className="position-relative"
          ref={containerRef}
          style={{
            width: DIM,
            height: DIM,
            borderRadius: 6,
            overflow: "hidden"
          }}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Grid overlay"
            crossOrigin="anonymous"
            style={{
              position: "absolute",
              top: imageOffset.y,
              left: imageOffset.x,
              width: imageDimensions.width * imageScale,
              height: imageDimensions.height * imageScale,
              objectFit: "contain"
            }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.removeAttribute("crossOrigin");
            }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          {renderGrid()}
          {hoveredColor && (
            <div className="color-preview-panel">
              <div>
                <KC_Text
                  text={colorKeyName}
                  size={"lg"}
                  weight={"bold"}
                  className={"text-black flex-grow-1"}
                />
              </div>
              <div className="d-flex justify-content-center gap-sm w-100">
                <div className="flex-grow-1"> &nbsp; </div>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    backgroundColor: hoveredColor,
                    border: "2px solid #ddd",
                    borderRadius: 4
                  }}
                />
                <div className="flex-grow-1"> &nbsp; </div>
                <span>{hoveredColor}</span>
                <div className="flex-grow-1"> &nbsp; </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {renderMagnifier()}
    </>
  );
};

export default ImageGrid;

interface ImageGridProps {
  imageUrl: string;
  gridSize?: number;
  onColorPick?: (color: string, x: number, y: number) => void;
  colorKeyName?: string;
  onClose: () => void;
}

interface GridPosition {
  row: number;
  col: number;
}

function getSurroundingPixels(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
) {
  const halfSize = Math.floor(size / 2);
  const pixelDataArray: any = [];

  // Ensure we stay within canvas boundaries
  const startX = Math.max(0, x - halfSize);
  const startY = Math.max(0, y - halfSize);

  for (let i = 0; i < size; i++) {
    pixelDataArray[i] = [];
    for (let j = 0; j < size; j++) {
      const pixelX = Math.min(ctx.canvas.width - 1, startX + j);
      const pixelY = Math.min(ctx.canvas.height - 1, startY + i);
      const pixelData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
      pixelDataArray[i].push(pixelData);
    }
  }
  return pixelDataArray;
}
